import { Component, OnInit } from '@angular/core';
import { AuthorizeService, IUser } from '../authorize.service';
import { Observable, of } from 'rxjs';
import { map, shareReplay, switchMap } from 'rxjs/operators';
import { ProfilePhotoService } from 'src/app/shared/profile-photo/profile-photo.service';

interface MenuUser {
  id: string | undefined;
  userName: string;
  displayName: string;
  initials: string;
  isAdministrator: boolean;
}

@Component({
  selector: 'app-login-menu',
  templateUrl: './login-menu.component.html',
  styleUrls: ['./login-menu.component.scss']
})
export class LoginMenuComponent implements OnInit {
  public isAuthenticated: Observable<boolean>;
  public userName: Observable<string>;
  public role: Observable<string>;
  public initials: Observable<string>;
  public user: Observable<MenuUser | null>;
  /** False until we know the user has a photo; drives the "Add a profile photo" nudge. */
  public hasPhoto: Observable<boolean>;

  /** Account settings › Profile, scrolled to the photo. */
  readonly photoSettingsUrl = '/Identity/Account/Manage#photo';

  constructor(private authorizeService: AuthorizeService, private photos: ProfilePhotoService) { }

  ngOnInit() {
    this.isAuthenticated = this.authorizeService.isAuthenticated();
    this.userName = this.authorizeService.getUser().pipe(map(u => u && u.name));
    this.role = this.authorizeService.getUser().pipe(map(u => u && u.role));
    this.initials = this.authorizeService.getUser().pipe(map(u => this.toInitials(u)));
    this.user = this.authorizeService.getUser().pipe(
      map(u => u ? this.toMenuUser(u) : null),
      shareReplay({ bufferSize: 1, refCount: true }));
    this.hasPhoto = this.user.pipe(
      switchMap(u => u ? this.photos.photoUrl(u.id) : of(null)),
      map(url => !!url));
  }

  private toMenuUser(user: IUser): MenuUser {
    const fullName = `${(user.given_name || '').trim()} ${(user.family_name || '').trim()}`.trim();
    return {
      id: user.sub,
      userName: user.name,
      displayName: fullName || user.name,
      initials: this.toInitials(user),
      isAdministrator: user.role === 'Administrator',
    };
  }

  // First+last initial from the profile's given_name/family_name claims,
  // falling back to the first letter of the username when names are missing.
  private toInitials(user: IUser | null): string {
    if (!user) {
      return '';
    }

    const first = (user.given_name || '').trim();
    const last = (user.family_name || '').trim();

    if (first || last) {
      return `${first.charAt(0)}${last.charAt(0)}`.toUpperCase();
    }

    return (user.name || '').charAt(0).toUpperCase();
  }
}
